// @ts-ignore
import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';

declare const qz: any;

// ─── Interfaces (movidas de etiqueta-config.component.ts) ────────────────────
// Compartilhadas por qualquer tela que precise imprimir uma etiqueta já
// configurada (hoje: tela de Configuração de Etiquetas e o modal de Dados do
// Transporte). Mantidas aqui para evitar que as duas telas fiquem com cópias
// divergentes das mesmas interfaces.

export interface EtiquetaCampo {
  id: string;
  tipo: 'campo' | 'texto_livre';
  fonte?: string;
  coluna?: string;
  valor?: string;
  label?: string;
  mostrarLabel?: boolean;
  alinhamento: 'esquerda' | 'centro' | 'direita';
  negrito: boolean;
  italico: boolean;
  tamanhoFonte: number;
  tipografia?: string;
  /** Fonte interna usada pela impressora quando o modo ZPL nativo estiver ativo. */
  fonteZpl?: '0' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H';
  larguraPct: number;
  paddingEsqMm?: number;
  paddingDirMm?: number;
  oculto?: boolean;
}

export interface EtiquetaLinha {
  id: string;
  campos: EtiquetaCampo[];
  alturaLinha?: number;
  margemTopMm?: number;
}

export interface EtiquetaMargens {
  superior: number;
  inferior: number;
  esquerda: number;
  direita: number;
}

export interface EtiquetaDados {
  logo: {
    mostrar: boolean;
    alinhamento: string;
    larguraPct: number;
    alturaMm: number;
    margemTopMm: number;
    margemBottomMm: number;
  };
  margens: EtiquetaMargens;
  /** @deprecated use margens */
  margemMm?: number;
  linhas: EtiquetaLinha[];
  rotacaoImpressao?: 0 | 90 | 180 | 270;
  /** @deprecated use rotacaoImpressao */
  imprimirGirado?: boolean;
}

export interface EtiquetaImpressoraInfo {
  nome: string;
  driver: string;
  densidade: string;
  padrao: boolean;
  fisica: boolean;
}

/** Dados de uma etiqueta pronta pra ser renderizada/impressa. */
export interface EtiquetaImpressaoContexto {
  nome: string;
  largura: number;   // mm
  altura: number;    // mm
  dados: EtiquetaDados;
  dadosReais: Record<string, Record<string, string>> | null;
  logoVisivelImpressao: boolean;
  /** Rotação efetiva (0/90/180/270). Se omitido, é derivada de dados.rotacaoImpressao/imprimirGirado. */
  rotacaoEfetiva?: number;
  /** Faz o preview HTML aproximar a fonte interna selecionada para o ZPL. */
  previewZpl?: boolean;
}

/**
 * Motor de renderização e impressão de etiquetas — extraído de
 * EtiquetaConfigComponent pra ser reaproveitado por qualquer outra tela que
 * precise imprimir uma etiqueta já cadastrada (ex.: botão Imprimir no modal
 * de Dados do Transporte).
 *
 * Este serviço é "burro" de propósito: não mexe em toast, estado de tela nem
 * localStorage de preferências específicas de uma tela — cada componente
 * decide como reagir a sucesso/erro. As únicas persistências aqui são a
 * impressora e o modo de impressão selecionados, que fazem sentido serem
 * compartilhados entre todas as telas que imprimem etiqueta.
 */
@Injectable({ providedIn: 'root' })
export class EtiquetaPrintService {

  readonly tipografiasCampo = [
    { valor: 'Arial', rotulo: 'Arial', css: 'Arial, sans-serif', peso: '400' },
    { valor: 'Arial Bold', rotulo: 'Arial Bold', css: 'Arial, sans-serif', peso: '700' },
    { valor: 'Arial Narrow', rotulo: 'Arial Narrow', css: "'Arial Narrow', Arial, sans-serif", peso: '400' },
    { valor: 'Arial Narrow Bold', rotulo: 'Arial Narrow Bold', css: "'Arial Narrow', Arial, sans-serif", peso: '700' },
    { valor: 'Arial Black', rotulo: 'Arial Black', css: "'Arial Black', Arial, sans-serif", peso: '900' }
  ] as const;

  readonly fontesZpl = [
    { codigo: '0', rotulo: '0 - Escalavel', css: "'Arial Narrow', Arial, sans-serif", peso: '700', fatorLargura: 0.54 },
    { codigo: 'A', rotulo: 'A - Compacta 9x5', css: "'Courier New', monospace", peso: '400', fatorLargura: 0.56 },
    { codigo: 'B', rotulo: 'B - Compacta 11x7', css: "'Courier New', monospace", peso: '400', fatorLargura: 0.64 },
    { codigo: 'C', rotulo: 'C - Bitmap 18x10', css: "'Courier New', monospace", peso: '400', fatorLargura: 0.56 },
    { codigo: 'D', rotulo: 'D - Bitmap 18x10', css: "'Courier New', monospace", peso: '700', fatorLargura: 0.56 },
    { codigo: 'E', rotulo: 'E - Grande 28x15', css: "'Courier New', monospace", peso: '700', fatorLargura: 0.54 },
    { codigo: 'F', rotulo: 'F - Alta 26x13', css: "'Arial Narrow', Arial, sans-serif", peso: '700', fatorLargura: 0.50 },
    { codigo: 'G', rotulo: 'G - Grande 60x40', css: 'Arial, sans-serif', peso: '700', fatorLargura: 0.67 },
    { codigo: 'H', rotulo: 'H - Media 21x13', css: 'Arial, sans-serif', peso: '700', fatorLargura: 0.62 }
  ] as const;

  private readonly CHAVE_IMPRESSORA = 'etiqueta-config.impressora';
  private readonly CHAVE_MODO = 'etiqueta-config.modo-impressao';

  // ─── Preferências compartilhadas (impressora / modo) ────────────────────

  get impressoraSalva(): string {
    return localStorage.getItem(this.CHAVE_IMPRESSORA) ?? '';
  }

  salvarImpressoraSelecionada(nome: string): void {
    if (nome) localStorage.setItem(this.CHAVE_IMPRESSORA, nome);
    else localStorage.removeItem(this.CHAVE_IMPRESSORA);
  }

  get modoImpressaoSalvo(): 'imagem' | 'zpl' | 'html' {
    const modo = localStorage.getItem(this.CHAVE_MODO);
    return modo === 'html' || modo === 'zpl' ? modo : 'imagem';
  }

  salvarModoImpressao(modo: 'imagem' | 'zpl' | 'html'): void {
    localStorage.setItem(this.CHAVE_MODO, modo);
  }

  // ─── Descoberta de impressoras (QZ Tray) ────────────────────────────────

  async listarImpressoras(): Promise<EtiquetaImpressoraInfo[]> {
    if (!qz.websocket.isActive()) {
      await qz.websocket.connect({ retries: 2, delay: 1 });
    }
    const detalhes: any[] = await qz.printers.details();
    return (Array.isArray(detalhes) ? detalhes : [detalhes])
      .filter(Boolean)
      .map((p: any) => ({
        nome: String(p.name ?? ''),
        driver: String(p.driver ?? ''),
        densidade: Array.isArray(p.density) ? p.density.join(', ') : String(p.density ?? ''),
        padrao: Boolean(p.default),
        fisica: p.physical !== false
      }))
      .filter(p => p.nome)
      .sort((a, b) => Number(b.padrao) - Number(a.padrao)
        || Number(b.fisica) - Number(a.fisica)
        || a.nome.localeCompare(b.nome));
  }

  async impressoraPadraoQz(): Promise<string> {
    try { return String(await qz.printers.getDefault() ?? ''); } catch { return ''; }
  }

  dpiImpressora(impressora: EtiquetaImpressoraInfo | null | undefined): number {
    const valores = (impressora?.densidade ?? '').match(/\d+(?:[.,]\d+)?/g) ?? [];
    for (const valor of valores) {
      const numero = Number(valor.replace(',', '.'));
      if (numero >= 150 && numero <= 1200) return numero;
      if (numero >= 6 && numero <= 50) return numero * 25.4;
    }
    return 203;
  }

  suportaZpl(impressora: EtiquetaImpressoraInfo | null | undefined, impressoraSelecionada: string): boolean {
    const descricao = `${impressora?.nome ?? impressoraSelecionada} ${impressora?.driver ?? ''}`;
    return /zebra|zdesigner|\bzpl\b/i.test(descricao);
  }

  // ─── Rotação efetiva ─────────────────────────────────────────────────────

  private rotacaoEfetiva(ctx: EtiquetaImpressaoContexto): number {
    if (ctx.rotacaoEfetiva !== undefined) return ctx.rotacaoEfetiva;
    const rotacao = this.normalizarRotacao(ctx.dados.rotacaoImpressao);
    if (rotacao !== null) return rotacao;
    return ctx.dados.imprimirGirado ? 180 : 0;
  }

  private normalizarRotacao(valor: unknown): 0 | 90 | 180 | 270 | null {
    const numero = Number(valor);
    if (!Number.isFinite(numero)) return null;
    const normalizada = ((numero % 360) + 360) % 360;
    return normalizada === 0 || normalizada === 90 || normalizada === 180 || normalizada === 270
      ? normalizada
      : null;
  }

  // ─── Geração de HTML (preview e impressão) ──────────────────────────────

  gerarHtml(ctx: EtiquetaImpressaoContexto, modoImpressao: boolean, logoSrc = 'assets/logo_global.png'): string {
    const mm2px = (mm: number) => `${mm * 3.7795}px`;
    const dados = ctx.dadosReais; // null em preview sem dados reais

    const valorCampo = (campo: EtiquetaCampo): string => {
      if (campo.tipo === 'texto_livre') return escapeHtml(campo.valor ?? '');
      const col = (campo.coluna ?? '').toUpperCase();
      const fonte = (campo.fonte ?? '').toUpperCase();
      const prefixo = campo.mostrarLabel && campo.label
        ? `<span style="font-weight:400;font-style:normal;color:#666">${escapeHtml(campo.label)} </span>`
        : '';
      let conteudo: string;
      if (dados && dados[fonte] && col && dados[fonte][col] !== undefined) {
        conteudo = escapeHtml(dados[fonte][col]);
      } else {
        conteudo = `<span style="color:#aaa">[${escapeHtml(col)}]</span>`;
      }
      return prefixo + conteudo;
    };

    let linhasHtml = '';

    if (ctx.dados.logo?.mostrar && ctx.logoVisivelImpressao) {
      const logoPct = ctx.dados.logo.larguraPct ?? 20;
      const mTop = ctx.dados.logo.margemTopMm ?? 0;
      const mBottom = ctx.dados.logo.margemBottomMm ?? 2;
      const logoH = ctx.dados.logo.alturaMm && ctx.dados.logo.alturaMm > 0
        ? (modoImpressao ? `${ctx.dados.logo.alturaMm}mm` : `${ctx.dados.logo.alturaMm * 3.7795}px`)
        : 'auto';
      const imgH = logoH === 'auto' ? 'height:auto' : `height:${logoH};object-fit:contain`;
      const styleTop = mTop > 0 ? `margin-top:${modoImpressao ? mTop + 'mm' : mTop * 3.7795 + 'px'};` : '';
      const styleBot = mBottom > 0 ? `margin-bottom:${modoImpressao ? mBottom + 'mm' : mBottom * 3.7795 + 'px'};` : '';
      linhasHtml += `<div style="text-align:${alinhar(ctx.dados.logo.alinhamento)};height:${logoH};overflow:hidden;background:#fff;${styleTop}${styleBot}isolation:isolate">
        <img src="${logoSrc}" alt="" style="width:${logoPct}%;max-width:${logoPct}%;${imgH};display:inline-block;-webkit-print-color-adjust:exact;print-color-adjust:exact">
      </div>`;
    }

    for (const linha of (ctx.dados.linhas ?? [])) {
      const h = linha.alturaLinha && linha.alturaLinha > 0 ? linha.alturaLinha : null;
      const mTop = linha.margemTopMm && linha.margemTopMm > 0 ? linha.margemTopMm : null;
      const styleH = h ? `height:${modoImpressao ? h + 'mm' : mm2px(h)};` : '';
      const styleMTop = mTop ? `margin-top:${modoImpressao ? mTop + 'mm' : mm2px(mTop)};` : '';
      linhasHtml += `<div style="display:flex;width:100%;${styleH}${styleMTop}align-items:flex-start;line-height:1.3;overflow:hidden">`;
      for (const campo of linha.campos) {
        if (campo.oculto) continue;
        const fs = campo.tamanhoFonte ?? 10;
        const tipografia = this.tipografiasCampo.find(f => f.valor === campo.tipografia) ?? this.tipografiasCampo[0];
        const fonteZpl = ctx.previewZpl ? this.fonteZpl(campo.fonteZpl) : null;
        const ff = fonteZpl?.css ?? tipografia.css;
        const fw = fonteZpl?.peso ?? (campo.negrito ? '700' : tipografia.peso);
        const fi = fonteZpl ? 'normal' : (campo.italico ? 'italic' : 'normal');
        const al = alinhar(campo.alinhamento);
        const w = campo.larguraPct ?? 100;
        const pEsq = campo.paddingEsqMm && campo.paddingEsqMm > 0
          ? (modoImpressao ? `${campo.paddingEsqMm}mm` : `${campo.paddingEsqMm * 3.7795}px`) : '0';
        const pDir = campo.paddingDirMm && campo.paddingDirMm > 0
          ? (modoImpressao ? `${campo.paddingDirMm}mm` : `${campo.paddingDirMm * 3.7795}px`) : '0';
        linhasHtml +=
          `<span style="display:inline-block;width:${w}%;font-size:${fs}px;` +
          `font-family:${ff};font-weight:${fw};font-style:${fi};text-align:${al};` +
          `padding-left:${pEsq};padding-right:${pDir};box-sizing:border-box;` +
          `overflow:hidden;white-space:nowrap;text-overflow:ellipsis">` +
          `${valorCampo(campo)}</span>`;
      }
      linhasHtml += `</div>`;
    }

    const m = ctx.dados.margens ?? { superior: 1, inferior: 1, esquerda: 1, direita: 1 };
    if (modoImpressao) {
      const W = ctx.largura, H = ctx.altura;
      const rot = this.rotacaoEfetiva(ctx);
      const pad = `padding: ${m.superior}mm ${m.direita}mm ${m.inferior}mm ${m.esquerda}mm;`;
      const etqBase = `box-sizing: border-box; font-family: Arial, sans-serif; overflow: hidden; ${pad}`;
      const pageBase = `@page etiqueta { size: ${W}mm ${H}mm; margin: 0; }
          @media print {
            @page { size: ${W}mm ${H}mm; margin: 0; }
            html, body { width: ${W}mm !important; height: ${H}mm !important; }
            body, .etq { page: etiqueta; }
          }
          *, *::before, *::after { box-sizing: border-box; }
          html, body { margin: 0 !important; padding: 0 !important; overflow: hidden;
            -webkit-print-color-adjust: exact; print-color-adjust: exact; }`;
      let style: string;

      if (rot === 90) {
        style = `${pageBase}
          html, body { width: ${W}mm; height: ${H}mm; }
          .etq { position: absolute; top: ${H}mm; left: 0;
                 width: ${H}mm; height: ${W}mm; ${etqBase}
                 transform: rotate(-90deg); transform-origin: 0 0; }`;
      } else if (rot === 180) {
        style = `${pageBase}
          html, body { width: ${W}mm; height: ${H}mm; }
          .etq { position: absolute; top: ${H}mm; left: ${W}mm;
                 width: ${W}mm; height: ${H}mm; ${etqBase}
                 transform: rotate(180deg); transform-origin: 0 0; }`;
      } else if (rot === 270) {
        style = `${pageBase}
          html, body { width: ${W}mm; height: ${H}mm; }
          .etq { position: absolute; top: 0; left: ${W}mm;
                 width: ${H}mm; height: ${W}mm; ${etqBase}
                 transform: rotate(90deg); transform-origin: 0 0; }`;
      } else {
        style = `${pageBase}
          html, body { width: ${W}mm; height: ${H}mm; }
          .etq { position: absolute; top: 0; left: 0;
                 width: ${W}mm; height: ${H}mm; ${etqBase} }`;
      }
      return `<!DOCTYPE html><html><head><meta charset="utf-8"><title></title><style>${style}</style></head><body><div class="etq">${linhasHtml}</div></body></html>`;
    }

    const pTop = m.superior * 3.7795;
    const pBot = m.inferior * 3.7795;
    const pEsq = m.esquerda * 3.7795;
    const pDir = m.direita * 3.7795;
    const W = ctx.largura * 3.7795;
    const H = ctx.altura * 3.7795;
    const rot = this.rotacaoEfetiva(ctx);
    const conteudoBase = `position:absolute;box-sizing:border-box;overflow:hidden;` +
      `padding:${pTop}px ${pDir}px ${pBot}px ${pEsq}px;font-family:Arial,sans-serif;` +
      `background:#fff;transform-origin:0 0;`;
    let conteudoStyle: string;

    if (rot === 90) {
      conteudoStyle = `${conteudoBase}top:${H}px;left:0;width:${H}px;height:${W}px;transform:rotate(-90deg);`;
    } else if (rot === 180) {
      conteudoStyle = `${conteudoBase}top:${H}px;left:${W}px;width:${W}px;height:${H}px;transform:rotate(180deg);`;
    } else if (rot === 270) {
      conteudoStyle = `${conteudoBase}top:0;left:${W}px;width:${H}px;height:${W}px;transform:rotate(90deg);`;
    } else {
      conteudoStyle = `${conteudoBase}top:0;left:0;width:${W}px;height:${H}px;`;
    }

    return `<div style="position:relative;width:${W}px;height:${H}px;background:#fff;overflow:hidden;">` +
           `<div style="${conteudoStyle}">${linhasHtml}</div></div>`;
  }

  // ─── Impressão ───────────────────────────────────────────────────────────

  /**
   * Tenta imprimir via QZ Tray na impressora indicada; lança erro se falhar
   * (não faz fallback sozinho — quem chama decide se cai pro navegador).
   */
  async imprimirViaQz(
    ctx: EtiquetaImpressaoContexto,
    impressoraSelecionada: string,
    modoImpressaoLocal: 'imagem' | 'zpl' | 'html',
    impressora: EtiquetaImpressoraInfo | null
  ): Promise<void> {
    if (!qz.websocket.isActive()) {
      await qz.websocket.connect({ retries: 2, delay: 1 });
    }

    if (modoImpressaoLocal === 'imagem') {
      await this.imprimirImagemDoPreview(ctx, impressoraSelecionada, impressora);
      return;
    }

    if (modoImpressaoLocal === 'zpl' && this.suportaZpl(impressora, impressoraSelecionada)) {
      await this.imprimirViaZpl(ctx, impressoraSelecionada, impressora);
      return;
    }

    const logoSrc = await this.carregarLogoParaImpressao(ctx);
    const html = this.gerarHtml(ctx, true, logoSrc);
    const config = qz.configs.create(impressoraSelecionada, {
      units: 'mm',
      size: { width: ctx.largura, height: ctx.altura, custom: true },
      margins: 0,
      scaleContent: false,
      jobName: `Etiqueta - ${ctx.nome}`
    });
    const dados = [{ type: 'pixel', format: 'html', flavor: 'plain', data: html }];
    await qz.print(config, dados);
  }

  private async imprimirImagemDoPreview(
    ctx: EtiquetaImpressaoContexto,
    impressoraSelecionada: string,
    impressora: EtiquetaImpressoraInfo | null
  ): Promise<void> {
    const dpi = this.dpiImpressora(impressora);
    const base64 = await this.renderizarPreviewEmPng(ctx, dpi);
    const config = qz.configs.create(impressoraSelecionada, {
      units: 'mm',
      size: { width: ctx.largura, height: ctx.altura, custom: true },
      margins: 0,
      scaleContent: true,
      interpolation: 'nearest-neighbor',
      colorType: 'grayscale',
      jobName: `Etiqueta fiel - ${ctx.nome}`
    });
    const dados = [{ type: 'pixel', format: 'image', flavor: 'base64', data: base64 }];
    await qz.print(config, dados);
  }

  private async renderizarPreviewEmPng(ctx: EtiquetaImpressaoContexto, dpi: number): Promise<string> {
    const host = document.createElement('div');
    host.style.position = 'fixed';
    host.style.left = '-10000px';
    host.style.top = '0';
    host.style.pointerEvents = 'none';
    host.innerHTML = this.gerarHtml(ctx, false);
    document.body.appendChild(host);

    try {
      const etiqueta = host.firstElementChild as HTMLElement | null;
      if (!etiqueta) throw new Error('Preview da etiqueta indisponivel');
      if (document.fonts?.ready) await document.fonts.ready;
      const imagens = Array.from(etiqueta.querySelectorAll('img'));
      await Promise.all(imagens.map(img => img.complete
        ? Promise.resolve()
        : new Promise<void>(resolve => {
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          })
      ));

      const canvas = await html2canvas(etiqueta, {
        backgroundColor: '#ffffff',
        scale: dpi / 96,
        width: ctx.largura * 3.7795,
        height: ctx.altura * 3.7795,
        useCORS: true,
        logging: false
      });
      return canvas.toDataURL('image/png').split(',')[1];
    } finally {
      host.remove();
    }
  }

  private async imprimirViaZpl(
    ctx: EtiquetaImpressaoContexto,
    impressoraSelecionada: string,
    impressora: EtiquetaImpressoraInfo | null
  ): Promise<void> {
    const config = qz.configs.create(impressoraSelecionada, {
      encoding: 'UTF-8',
      jobName: `Etiqueta ZPL - ${ctx.nome}`
    });
    const dados = [{ type: 'raw', format: 'command', flavor: 'plain', data: this.gerarZpl(ctx, impressora) }];
    await qz.print(config, dados);
  }

  gerarZpl(ctx: EtiquetaImpressaoContexto, impressora: EtiquetaImpressoraInfo | null): string {
    const dpi = this.dpiImpressora(impressora);
    const pontosPorMm = dpi / 25.4;
    const larguraPontos = Math.max(1, Math.round(ctx.largura * pontosPorMm));
    const alturaPontos = Math.max(1, Math.round(ctx.altura * pontosPorMm));
    const rotacao = this.rotacaoEfetiva(ctx);
    const larguraLayout = rotacao === 90 || rotacao === 270 ? ctx.altura : ctx.largura;
    const alturaLayout = rotacao === 90 || rotacao === 270 ? ctx.largura : ctx.altura;
    const margens = ctx.dados.margens ?? { superior: 1, inferior: 1, esquerda: 1, direita: 1 };
    const larguraUtil = Math.max(1, larguraLayout - margens.esquerda - margens.direita);
    const comandos: string[] = [
      '^XA',
      '^CI28',
      `^PW${larguraPontos}`,
      `^LL${alturaPontos}`,
      '^LH0,0',
      '^LS0',
      '^PON'
    ];
    let y = margens.superior;

    for (const linha of ctx.dados.linhas ?? []) {
      y += Math.max(0, linha.margemTopMm ?? 0);
      let x = margens.esquerda;
      let maiorAlturaMm = 0;

      for (const campo of linha.campos ?? []) {
        if (campo.oculto) continue;
        const larguraCampo = larguraUtil * Math.max(0, campo.larguraPct ?? 100) / 100;
        const paddingEsq = Math.max(0, campo.paddingEsqMm ?? 0);
        const paddingDir = Math.max(0, campo.paddingDirMm ?? 0);
        const larguraTextoMm = Math.max(0.5, larguraCampo - paddingEsq - paddingDir);
        const texto = this.valorCampoParaImpressao(ctx, campo);
        const fonteZpl = this.fonteZpl(campo.fonteZpl);
        let alturaFonte = Math.max(12, Math.round((campo.tamanhoFonte ?? 10) * dpi / 96));
        let larguraFonte = Math.max(6, Math.round(alturaFonte * fonteZpl.fatorLargura));
        const larguraDisponivel = Math.max(1, Math.round(larguraTextoMm * pontosPorMm));
        const larguraEstimada = Array.from(texto).length * larguraFonte * 0.58;
        if (larguraEstimada > larguraDisponivel && larguraEstimada > 0) {
          const fator = larguraDisponivel / larguraEstimada;
          alturaFonte = Math.max(12, Math.floor(alturaFonte * fator));
          larguraFonte = Math.max(6, Math.floor(larguraFonte * fator));
        }

        const alturaFonteMm = alturaFonte / pontosPorMm;
        maiorAlturaMm = Math.max(maiorAlturaMm, alturaFonteMm * 1.3);
        const retangulo = this.transformarRetanguloZpl(
          x + paddingEsq,
          y,
          larguraTextoMm,
          Math.max(alturaFonteMm * 1.3, linha.alturaLinha ?? 0),
          rotacao,
          ctx.largura,
          ctx.altura
        );
        const alinhamento = campo.alinhamento === 'centro' ? 'C' : campo.alinhamento === 'direita' ? 'R' : 'L';
        const orientacao = rotacao === 90 ? 'B' : rotacao === 180 ? 'I' : rotacao === 270 ? 'R' : 'N';
        comandos.push(
          `^FO${Math.round(retangulo.x * pontosPorMm)},${Math.round(retangulo.y * pontosPorMm)}` +
          `^A${fonteZpl.codigo}${orientacao},${alturaFonte},${larguraFonte}` +
          `^FB${larguraDisponivel},1,0,${alinhamento},0` +
          `^FH_^FD${this.escaparTextoZpl(texto)}^FS`
        );
        x += larguraCampo;
      }

      y += linha.alturaLinha && linha.alturaLinha > 0
        ? linha.alturaLinha
        : Math.max(maiorAlturaMm, 1.5);
      if (y > alturaLayout - margens.inferior) break;
    }

    comandos.push('^XZ');
    return comandos.join('\n');
  }

  private fonteZpl(codigo: string | null | undefined): (typeof this.fontesZpl)[number] {
    const normalizado = String(codigo ?? '0').toUpperCase();
    return this.fontesZpl.find(fonte => fonte.codigo === normalizado) ?? this.fontesZpl[0];
  }

  private valorCampoParaImpressao(ctx: EtiquetaImpressaoContexto, campo: EtiquetaCampo): string {
    if (campo.tipo === 'texto_livre') return campo.valor ?? '';
    const coluna = (campo.coluna ?? '').toUpperCase();
    const fonte = (campo.fonte ?? '').toUpperCase();
    const valor = ctx.dadosReais?.[fonte]?.[coluna] ?? `[${coluna}]`;
    return campo.mostrarLabel && campo.label ? `${campo.label} ${valor}` : String(valor);
  }

  private transformarRetanguloZpl(
    x: number,
    y: number,
    largura: number,
    altura: number,
    rotacao: number,
    larguraPapel: number,
    alturaPapel: number
  ): { x: number; y: number } {
    if (rotacao === 90) return { x: y, y: Math.max(0, alturaPapel - x - largura) };
    if (rotacao === 180) return {
      x: Math.max(0, larguraPapel - x - largura),
      y: Math.max(0, alturaPapel - y - altura)
    };
    if (rotacao === 270) return { x: Math.max(0, larguraPapel - y - altura), y: x };
    return { x, y };
  }

  private escaparTextoZpl(texto: string): string {
    return Array.from(new TextEncoder().encode(texto)).map(byte => {
      const caractere = String.fromCharCode(byte);
      return byte >= 32 && byte <= 126 && caractere !== '^' && caractere !== '~' && caractere !== '_'
        ? caractere
        : `_${byte.toString(16).toUpperCase().padStart(2, '0')}`;
    }).join('');
  }

  /**
   * Abre o diálogo de impressão do navegador. Lança erro (com mensagem em
   * português já pronta pra exibir num toast) se não conseguir preparar.
   */
  async imprimirNoNavegador(ctx: EtiquetaImpressaoContexto): Promise<void> {
    const w = window.open('', '_blank');
    if (!w) {
      throw new Error('Permita a abertura da janela para imprimir.');
    }

    const fecharJanela = () => {
      if (!w.closed) w.close();
    };

    try {
      const logoSrc = await this.carregarLogoParaImpressao(ctx);
      const html = this.gerarHtml(ctx, true, logoSrc);
      w.document.open();
      w.document.write(html);
      w.document.close();

      await this.aguardarDocumentoImpressao(w);
      w.addEventListener('afterprint', fecharJanela, { once: true });
      w.focus();
      w.print();

      window.setTimeout(fecharJanela, 250);
    } catch {
      fecharJanela();
      throw new Error('Não foi possível preparar a impressão.');
    }
  }

  private async carregarLogoParaImpressao(ctx: EtiquetaImpressaoContexto): Promise<string> {
    const assetUrl = new URL('assets/logo_global.png', document.baseURI).href;
    if (!ctx.dados.logo?.mostrar || !ctx.logoVisivelImpressao) return assetUrl;

    try {
      const resposta = await fetch(assetUrl);
      if (!resposta.ok) return assetUrl;
      const blob = await resposta.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    } catch {
      return assetUrl;
    }
  }

  private aguardarDocumentoImpressao(w: Window): Promise<void> {
    return new Promise(resolve => {
      const concluir = () => {
        const imagens = Array.from(w.document.images);
        Promise.all(imagens.map(img => img.complete
          ? Promise.resolve()
          : new Promise<void>(fim => {
              img.addEventListener('load', () => fim(), { once: true });
              img.addEventListener('error', () => fim(), { once: true });
            })
        )).then(() => resolve());
      };

      if (w.document.readyState === 'complete') concluir();
      else w.addEventListener('load', concluir, { once: true });
    });
  }

  /** Gera e abre um PDF da etiqueta em nova aba (usa jsPDF via CDN, carregado sob demanda). */
  async imprimirPdf(ctx: EtiquetaImpressaoContexto): Promise<void> {
    if (!(window as any).jspdf?.jsPDF) {
      await new Promise<void>((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Falha ao carregar jsPDF do CDN'));
        document.head.appendChild(s);
      });
    }
    const JsPDF = (window as any).jspdf.jsPDF;
    const W = ctx.largura;
    const H = ctx.altura;
    const rot = this.rotacaoEfetiva(ctx);
    const m = ctx.dados.margens ?? { superior: 1, inferior: 1, esquerda: 1, direita: 1 };
    const dadosR = ctx.dadosReais;

    const isSwapped = rot === 90 || rot === 270;
    const pW = isSwapped ? H : W;
    const pH = isSwapped ? W : H;

    const pdfOrientation: 'landscape' | 'portrait' = pW >= pH ? 'landscape' : 'portrait';
    const doc = new JsPDF({ unit: 'mm', format: [pW, pH], orientation: pdfOrientation });

    const tf = (lx: number, ly: number): [number, number] => {
      if (rot === 90) return [ly, W - lx];
      if (rot === 180) return [W - lx, H - ly];
      if (rot === 270) return [H - ly, lx];
      return [lx, ly];
    };

    const availW = W - m.esquerda - m.direita;
    let curY = m.superior;

    if (ctx.dados.logo?.mostrar && ctx.logoVisivelImpressao) {
      const logoH = (ctx.dados.logo.alturaMm ?? 0) > 0 ? ctx.dados.logo.alturaMm : 8;
      const mTop = ctx.dados.logo.margemTopMm ?? 0;
      const mBot = ctx.dados.logo.margemBottomMm ?? 2;
      curY += mTop;
      if (rot === 0) {
        try {
          const resp = await fetch('assets/logo_global.png');
          const ab = await resp.arrayBuffer();
          const b64 = btoa(String.fromCharCode(...new Uint8Array(ab)));
          const logoPct = ctx.dados.logo.larguraPct ?? 20;
          const logoW = availW * logoPct / 100;
          let logoX = m.esquerda;
          if (ctx.dados.logo.alinhamento === 'centro') logoX = m.esquerda + (availW - logoW) / 2;
          if (ctx.dados.logo.alinhamento === 'direita') logoX = W - m.direita - logoW;
          doc.addImage(`data:image/png;base64,${b64}`, 'PNG', logoX, curY, logoW, logoH);
        } catch { /* logo não carregou — ignora */ }
      }
      curY += logoH + mBot;
    }

    for (const linha of ctx.dados.linhas) {
      curY += linha.margemTopMm ?? 0;
      const fixedH = linha.alturaLinha && linha.alturaLinha > 0 ? linha.alturaLinha : 0;
      let xCursor = 0;
      let maxFsMm = 0;

      for (const campo of linha.campos) {
        if (campo.oculto) continue;

        const fsPx = campo.tamanhoFonte ?? 10;
        const fsPt = fsPx * 0.75;

        doc.setFont('helvetica',
          campo.negrito && campo.italico ? 'bolditalic' :
          campo.negrito ? 'bold' :
          campo.italico ? 'italic' : 'normal');
        doc.setFontSize(fsPt);

        const remainingAvailW = availW - xCursor;
        const fieldW = Math.min(
          availW * ((campo.larguraPct ?? 100) / 100),
          remainingAvailW
        );
        const pEsq = campo.paddingEsqMm ?? 0;
        const pDir = campo.paddingDirMm ?? 0;
        const maxW = Math.max(fieldW - pEsq - pDir, 1);

        let text = campo.tipo === 'texto_livre'
          ? (campo.valor ?? '')
          : (() => {
              const col = (campo.coluna ?? '').toUpperCase();
              const fonte = (campo.fonte ?? '').toUpperCase();
              const val = dadosR?.[fonte]?.[col] ?? `[${col}]`;
              return (campo.mostrarLabel && campo.label) ? `${campo.label} ${val}` : val;
            })();

        const FONT_SAFETY = 1.08;

        const rawW = (doc.getTextWidth(text) as number) * FONT_SAFETY;
        let effectiveFsPt = fsPt;
        if (rawW > maxW && rawW > 0) {
          effectiveFsPt = Math.max(4, fsPt * (maxW / rawW) * 0.97);
          doc.setFontSize(effectiveFsPt);
        }
        while (text.length > 1 && (doc.getTextWidth(text) as number) * FONT_SAFETY > maxW) {
          text = text.slice(0, -1);
        }

        const effectiveFsMm = effectiveFsPt * (25.4 / 72);

        const tw = doc.getTextWidth(text) as number;
        let lx = m.esquerda + xCursor + pEsq;
        if (campo.alinhamento === 'centro') lx += (maxW - tw) / 2;
        if (campo.alinhamento === 'direita') lx += maxW - tw;

        const ly = curY + effectiveFsMm;

        const [px, py] = tf(lx, ly);
        doc.text(text, px, py, { angle: rot } as any);

        xCursor += fieldW;
        maxFsMm = Math.max(maxFsMm, effectiveFsMm);
      }

      curY += fixedH > 0 ? fixedH : maxFsMm + 1.5;
    }

    const blob: Blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function alinhar(a: string | undefined): string {
  if (a === 'centro') return 'center';
  if (a === 'direita') return 'right';
  return 'left';
}
